import React, {useRef, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {getToken, Helper} from '@tickchak/react-share/app';
import {useParagraphEditor} from '../../hooks/useParagraphEditor';
import {isContentEmpty, focusNextEditableElement, splitTextToNextRow, mergeWithPreviousRow} from '../../utils/dom';
import * as css from './ParagraphRow.css';
    
interface ParagraphRowProps {
    structureIndex: number;
    content: string;
    onChange?: (content: string) => void;
    onFocus?: () => void;
    setLastFocusedEditable?: (element: HTMLElement | null) => void;
    updateSavedCursorOffset?: () => void;
}

function ParagraphRow({structureIndex, content, onChange, onFocus, setLastFocusedEditable, updateSavedCursorOffset}: ParagraphRowProps) {
    const {t} = useTranslation('whatsapp');
    const contentEditableRef = useRef<HTMLDivElement>(null);

    const isEmpty = isContentEmpty(content);

    const handleEnterKeySplit = useCallback(() => {
        if(contentEditableRef.current) {
            return splitTextToNextRow(contentEditableRef.current, onChange);
        }
        return false;
    }, [onChange]);

    const handleEnterKey = () => {
        if(contentEditableRef.current) {
            focusNextEditableElement(contentEditableRef.current);
        }
    };

    const handleBackspaceKeyMerge = useCallback(() => {
        if(contentEditableRef.current) {
            return mergeWithPreviousRow(contentEditableRef.current, onChange);
        }
        return false;
    }, [onChange]);

    const {handleInput, handleKeyDown, handlePaste, handleBlur, handleFocus, handleCursorMove} = useParagraphEditor({
        content,
        contentEditableRef,
        onChange,
        onFocus,
        onEditableFocus: setLastFocusedEditable,
        onEditableCursorMove: updateSavedCursorOffset,
        onEnterKeySplit: handleEnterKeySplit,
        onEnterKey: handleEnterKey,
        onBackspaceKeyMerge: handleBackspaceKeyMerge,
        enableMultiLinePaste: true
    });

    return (
        <css.row>
            <css.contentEditable
                ref={contentEditableRef}
                contentEditable
                data-cy={`whatsapp-template-text-${structureIndex}`}
                data-structure-index={structureIndex}
                data-is-empty={String(isEmpty)}
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onFocus={handleFocus}
                onKeyUp={handleCursorMove}
                onMouseUp={handleCursorMove}
                onClick={handleCursorMove}
                onBlur={handleBlur}
                suppressContentEditableWarning
            />
            {isEmpty && (
                <Helper
                    content={t('emptyParagraphWarning')}
                    size='small'
                    maxWidth={240}
                    icon={'!'}
                    bgColor={getToken('color.state.warning.main')}
                />
            )}
        </css.row>
    );
}

export default ParagraphRow;
